import { FormikProps } from "formik";
import { useSearchParams } from "react-router-dom";
import Flex from "../../../../../../../../../../components/Flex";
import { useReduceDisclosure } from "../../../../../../../../../../hooks/use-reduce-disclosure";
import { useRosPeTemplateArchive } from "../../../../../../../../../../queries/encounter/use-ros-pe-template-archive";
import { useRosPeTemplateList } from "../../../../../../../../../../queries/encounter/use-ros-pe-template-list";
import { usePatientVitalsList } from "../../../../../../../../../../queries/vitals/use-patient-vitals-list";
import {
  getXtenantId,
  DEF_PAGINATION,
} from "../../../../../../../../../../utils/utils";
import PeImportDrawer from "../../../../../../../scheduling/appointment/encounter/default-forms/PeImportDrawer";
import RosImportDrawer from "../../../../../../../scheduling/appointment/encounter/default-forms/RosImportDrawer";
import { StyleAddButton } from "../../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import UpsertVitalFormDrawerNew from "../../../../vitals/UpsertVitalFormDrawerNew";
import PreviewContentWrapper from "./PreviewContentWrapper";
import VitalsCommonTable from "./VitalsCommonTable";
import Text from "../../../../../../../../../../components/Text";

interface Props {
  formik: FormikProps<any>;
}

const PrimaryCareSoapNoteObjectives = (_: Props) => {
  // const { formik } = props;
  const [addVitalDisclosure, templateDisclosure, rosTemplateDisclosure] =
    useReduceDisclosure(3);

  const appointmentid = useSearchParams()[0].get("appointmentid");
  const { mutateAsync: archiveTemplate } = useRosPeTemplateArchive();

  function peTemplateArchive<T>(value: boolean, id: T) {
    return archiveTemplate({
      encounterTemplateId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId(),
    }).then((res) => {
      refetchPeTemplates();
      return res;
    });
  }
  function rosTemplateArchive<T>(value: boolean, id: T) {
    return archiveTemplate({
      encounterTemplateId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId(),
    }).then((res) => {
      refetchRosTemplates();
      return res;
    });
  }

  const { data: peTemplates, refetch: refetchPeTemplates } =
    useRosPeTemplateList({
      pagination: {
        page: 0,
        pageSize: 50,
      },
      gridSorting: [],
      archive: false,
      status: true,
      templateType: "PHYSICAL_EXAM",
      appointmentUuid: appointmentid ?? undefined,
      xTENANTID: getXtenantId(),
    });
  const { data: rosTemplates, refetch: refetchRosTemplates } =
    useRosPeTemplateList({
      pagination: { page: 0, pageSize: 50 },
      gridSorting: [],
      archive: false,
      status: true,
      templateType: "ROS",
      appointmentUuid: appointmentid ?? undefined,
      xTENANTID: getXtenantId(),
    });

  const filteredPeTemplates = peTemplates?.data?.content?.filter(
    (d: any) => !d.archive && !!d?.description?.toString().trim()
  );
  const filteredRosTemplates = rosTemplates?.data?.content?.filter(
    (d: any) => !d.archive && !!d?.description?.toString().trim()
  );
  const { data: vitalsList } = usePatientVitalsList({
    pagination: DEF_PAGINATION,
    gridSorting: [],
    archive: false,
    appointmentUuid: appointmentid ?? undefined,
  });
  return (
    <>
      {<VitalsCommonTable vitalsList={vitalsList?.data?.content} />}
      {!!filteredPeTemplates?.length && (
        <PreviewContentWrapper
          dataList={filteredPeTemplates}
          k="peTemplate"
          title="PE Templates"
          renderContent={(peTemplate: any) => (
            <Text sx={{ color: "#424242" }}>
              {`${peTemplate?.templateNameType ?? "-"} : ${
                peTemplate?.description ?? "-"
              }`}
            </Text>
          )}
          onDelete={(uuid) => {
            peTemplateArchive(true, uuid).then(() => {});
          }}
          onEdit={(peTemplate) => {
            templateDisclosure.open(peTemplate);
          }}
        />
      )}
      {!!filteredRosTemplates?.length && (
        <PreviewContentWrapper
          dataList={filteredRosTemplates}
          k="rosTemplate"
          title="ROS Templates"
          renderContent={(rosTemplate: any) => (
            <Text sx={{ color: "#424242" }}>
              {`${rosTemplate?.templateNameType ?? "-"} : ${
                rosTemplate?.description ?? "-"
              }`}
            </Text>
          )}
          onDelete={(uuid) => {
            rosTemplateArchive(true, uuid).then(() => {});
          }}
          onEdit={(rosTemplate) => {
            templateDisclosure.open(rosTemplate);
          }}
        />
      )}
      {/* Action Buttons */}
      {/* note: need to replace this button with mapper for all the buttons */}
      <Flex gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <StyleAddButton
          w="100px"
          onClick={() => {
            addVitalDisclosure.open();
          }}
          label="Add Vitals"
        />
        {/* result button is removed as it has now functionality */}
        {/* <StyleAddButton w="80px" onClick={() => {}} label="Results" /> */}
        {(rosTemplates?.data?.content?.length ?? 0) < 1 && (
          <StyleAddButton
            w="100px"
            onClick={() => rosTemplateDisclosure.open()}
            label="Add ROS"
          />
        )}
        {(peTemplates?.data?.content?.length ?? 0) < 1 && (
          <StyleAddButton
            w="80px"
            onClick={() => templateDisclosure.open()}
            label="Add PE"
          />
        )}
      </Flex>

      <UpsertVitalFormDrawerNew disclosureHost={addVitalDisclosure} />
      <PeImportDrawer disclosureHost={templateDisclosure} />
      <RosImportDrawer disclosureHost={rosTemplateDisclosure} />
    </>
  );
};

export default PrimaryCareSoapNoteObjectives;
